import React, { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Printer, Image as ImageIcon, Activity, Loader2, CheckCircle2, ReceiptText, MessageCircle } from "lucide-react";
import { api, STATUSES, fmtDate, fmtDateTime, COMPANY } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { InvoiceDialog, rupiah } from "@/components/InvoiceDialog";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const AdminBoard = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState("ALL");
  const [cariClient, setCariClient] = useState("");
  const [rows, setRows] = useState([]);
  const [acts, setActs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [invoiceJo, setInvoiceJo] = useState(null);

  const load = useCallback(async () => {
    const [b, a] = await Promise.all([
      api.get("/admin-board", { params: filter === "ALL" ? {} : { admin_status: filter } }),
      api.get("/admin-activities"),
    ]);
    setRows(b.data);
    setActs(a.data);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const view = rows.filter(
    (jo) =>
      !cariClient.trim() ||
      (jo.client_name || "").toLowerCase().includes(cariClient.trim().toLowerCase())
  );

  const setStatus = async (jo, status) => {
    setBusy(jo.id);
    try {
      await api.put(`/job-orders/${jo.id}/admin-status`, { status });
      toast.success(`JO ${jo.jo_number} · admin ${status} · masuk papan produksi`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan status admin");
    } finally {
      setBusy(null);
    }
  };

  const waOrder = async (jo) => {
    try {
      const { data } = await api.get(`/job-orders/${jo.id}/wa-jo`);
      window.open(data.link, "_blank");
      toast.success(`Pesan WA order JO ${jo.jo_number} disiapkan`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyiapkan pesan WA");
    }
  };

  const printJoThermal = async (jo) => {
    const w = window.open("", "_blank", "width=420,height=800");
    if (!w) return toast.error("Popup diblokir browser");
    const doc = w.document;
    doc.title = `JO ${jo.jo_number}`;
    const style = doc.createElement("style");
    style.textContent = `@page { size: 58mm auto; margin: 2mm; }
      body { font-family: monospace; font-size: 11px; color:#000; margin:0; padding:4px; width:54mm; }
      .c { text-align:center; }
      .b { font-weight:700; }
      .sep { border-top:1px dashed #000; margin:4px 0; }
      .row { display:flex; justify-content:space-between; gap:4px; }
      img { width: 40mm; display:block; margin:0 auto; }`;
    doc.head.appendChild(style);
    const rincian = (jo.details || []).length
      ? (jo.details || [])
          .map(
            (d) =>
              `<div>• ${d.kategori || "-"} / ${d.jenis_order || "-"}<br/>&nbsp;&nbsp;${
                d.deskripsi || "-"
              }<br/>&nbsp;&nbsp;${d.panjang || "-"} x ${d.lebar || "-"} · qty ${d.qty || "-"}</div>`
          )
          .join("")
      : (jo.items || []).map((i) => `<div>• ${i.kategori}</div>`).join("");
    doc.body.innerHTML = `
      <div class="c"><img src="${COMPANY.logo}" alt="logo"/>
        <div class="b">${COMPANY.name}</div>
        <div style="font-size:8px">${COMPANY.tagline}</div>
        <div style="font-size:8px">${COMPANY.address}</div>
        <div style="font-size:8px">Telp/WA: ${COMPANY.phone}</div>
        <div style="font-size:8px">${COMPANY.email}</div>
      </div>
      <div class="sep"></div>
      <div class="b c">JOB ORDER</div>
      <div>Nama Client : ${jo.client_name || "-"}</div>
      <div>No JO : ${jo.jo_number}</div>
      <div>Tanggal : ${jo.tanggal || "-"}</div>
      <div>Deskripsi : ${jo.deskripsi || "-"}</div>
      <div class="sep"></div>
      <div class="b">RINCIAN ORDER</div>
      ${rincian}
      <div class="sep"></div>
      <div>Catatan : ${jo.catatan || "-"}</div>
      <div>User entri : ${jo.created_by || "-"}</div>
      <div>Dicetak oleh : ${user?.username || "-"}</div>
      <div class="sep"></div>
      <div class="c" style="font-size:9px">Terima kasih</div>`;
    w.setTimeout(() => w.print(), 500);
    try {
      const { data } = await api.get(`/job-orders/${jo.id}/wa-jo`);
      window.open(data.link, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyiapkan pesan WA");
    }
    try {
      await api.post(`/job-orders/${jo.id}/print-log`);
      load();
    } catch (error) {
      logError("Gagal mencatat aktivitas cetak:", error);
    }
  };

  const viewFile = async (id) => {
    try {
      const res = await api.get(`/files/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Gagal membuka foto");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-board">
      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-5 h-5 text-blue-400" />
            <div>
              <h3 className="font-display text-xl font-semibold">Papan Admin</h3>
              <p className="text-xs text-slate-400 mt-1">
                Setiap JO baru wajib diberi status admin di sini dulu, baru muncul di Papan Produksi ·{" "}
                <span className="text-blue-300 font-semibold" data-testid="admin-board-total">
                  {rows.length} JO
                </span>
              </p>
            </div>
          </div>
          <input
            data-testid="admin-client-filter"
            placeholder="Filter nama client"
            value={cariClient}
            onChange={(e) => setCariClient(e.target.value)}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            data-testid="admin-board-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Semua JO</option>
            <option value="BELUM">Belum Di-ACC</option>
            <option value="MASUK">Sudah Di-ACC (MASUK)</option>
          </select>
        </div>
      </div>

      {view.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="admin-board-empty">
          Tidak ada job order pada filter ini.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {view.map((jo) => {
          const released = STATUSES.includes(jo.admin_status);
          return (
            <div key={jo.id} data-testid={`admin-jo-${jo.jo_number}`} className="glass rounded-2xl p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-semibold text-slate-100">
                    JO {jo.jo_number}{" "}
                    <span className="text-xs font-normal text-slate-500">· {fmtDate(jo.tanggal)}</span>
                  </div>
                  <div className="text-sm text-slate-300">{jo.client_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{jo.deskripsi}</div>
                  <div className="mt-2 text-[11px] text-slate-400" data-testid={`admin-jo-user-${jo.jo_number}`}>
                    Username entri: <b className="text-blue-300">{jo.created_by || "-"}</b>
                    {jo.admin_by && <> · admin: <b className="text-amber-300">{jo.admin_by}</b></>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">ACC Admin</div>
                  <Button
                    data-testid={`admin-status-${jo.jo_number}`}
                    onClick={() => setStatus(jo, "MASUK")}
                    disabled={busy === jo.id || released}
                    className="mt-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-4 text-xs font-semibold disabled:opacity-40"
                  >
                    {busy === jo.id ? <Loader2 className="w-4 h-4 animate-spin" /> : released ? "Sudah ACC" : "ACC MASUK"}
                  </Button>
                  <div className="mt-2 text-[10px] uppercase tracking-wider">
                    {released ? (
                      <span className="text-emerald-300 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> masuk papan produksi
                      </span>
                    ) : (
                      <span className="text-amber-300">belum dirilis</span>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid={`admin-invoice-btn-${jo.jo_number}`}
                    onClick={() => setInvoiceJo(jo)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-blue-400/30 bg-blue-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-blue-200 hover:bg-blue-500/20 transition-colors duration-200"
                  >
                    <ReceiptText className="w-3.5 h-3.5" />
                    {jo.invoice ? "Ubah Faktur" : "Entri Faktur"}
                  </button>
                  {jo.invoice && (
                    <div
                      className="mt-1.5 text-[10px] text-emerald-300"
                      data-testid={`admin-invoice-total-${jo.jo_number}`}
                    >
                      Total: <b>{rupiah(jo.invoice.total)}</b> · {jo.invoice.lines?.length || 0} item
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                {jo.jo_photo_file_id && (
                  <button
                    data-testid={`admin-view-jo-photo-${jo.jo_number}`}
                    onClick={() => viewFile(jo.jo_photo_file_id)}
                    className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 transition-colors duration-200"
                  >
                    <ImageIcon className="w-4 h-4" /> Foto JO
                  </button>
                )}
                {(jo.items || [])
                  .filter((i) => i.photo_file_id)
                  .map((i) => (
                    <button
                      key={i.kategori}
                      data-testid={`admin-view-kat-photo-${jo.jo_number}-${i.kategori}`}
                      onClick={() => viewFile(i.photo_file_id)}
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors duration-200"
                    >
                      <ImageIcon className="w-4 h-4" /> {i.kategori}
                    </button>
                  ))}
                <Button
                  data-testid={`admin-wa-${jo.jo_number}`}
                  onClick={() => waOrder(jo)}
                  className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 text-xs font-semibold"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> WA Order ke Client
                </Button>
                <Button
                  data-testid={`admin-print-${jo.jo_number}`}
                  onClick={() => printJoThermal(jo)}
                  className="ml-auto h-9 rounded-xl bg-blue-600 hover:bg-blue-500 px-4 text-xs font-semibold"
                >
                  {busy === jo.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-2" /> Print JO 58mm + WA</>}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-5 md:p-6 border-b border-white/10 flex items-center gap-3">
          <Activity className="w-5 h-5 text-blue-400" />
          <h3 className="font-display text-xl font-semibold">
            Riwayat Aktivitas Menu Admin{" "}
            <span className="text-slate-500 text-sm font-normal">({acts.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="admin-activity-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-slate-400">
                <th className="px-5 py-4 font-semibold">Tanggal & Waktu</th>
                <th className="px-5 py-4 font-semibold">Akun</th>
                <th className="px-5 py-4 font-semibold">Aktivitas</th>
                <th className="px-5 py-4 font-semibold">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {acts.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-slate-500">
                    Belum ada aktivitas di menu admin.
                  </td>
                </tr>
              )}
              {acts.map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-blue-500/5 transition-colors duration-200">
                  <td className="px-5 py-3.5 text-slate-300 whitespace-nowrap">{fmtDateTime(a.at)}</td>
                  <td className="px-5 py-3.5 text-slate-200">{a.username}</td>
                  <td className="px-5 py-3.5">
                    <span className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200">
                      {a.action}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <InvoiceDialog
        jo={invoiceJo}
        open={!!invoiceJo}
        onOpenChange={(o) => !o && setInvoiceJo(null)}
        onSaved={load}
      />
    </div>
  );
};
